'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { HiXMark } from 'react-icons/hi2';
import { SITE_CONFIG } from '@/lib/seo';

// next/image bez remotePatterns optymalizuje tylko obrazy z własnej domeny/ścieżek względnych.
// Pole image_url w panelu admina to dowolny wklejony URL — dla obcego originu zostajemy przy <img>.
function isOwnOriginImage(url: string): boolean {
  if (url.startsWith('/')) return true;
  return url.startsWith(SITE_CONFIG.url);
}

interface PopupConfig {
  enabled: boolean;
  title: string;
  content: string;
  image_url: string;
  button_text: string;
  background_color: string;
  text_color: string;
  button_color: string;
  button_text_color: string;
  overlay_color: string;
  width: string;
  show_on_every_visit: boolean;
  show_close_button: boolean;
  auto_close_seconds: number;
}

const POPUP_DISMISSED_KEY = 'libra_popup_dismissed';

// Basic HTML sanitizer - allows only safe tags and attributes
function sanitizeHtml(html: string): string {
  // Convert line breaks to <br>
  let sanitized = html.replace(/\n/g, '<br>');

  // Allow only safe tags
  const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'ul', 'ol', 'li', 'a', 'span'];

  // Remove any script tags and event handlers completely
  sanitized = sanitized.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');

  // Remove any tags that aren't in our allowed list (keep the content)
  const tagRegex = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
  sanitized = sanitized.replace(tagRegex, (match, tagName) => {
    const tag = tagName.toLowerCase();
    if (allowedTags.includes(tag)) {
      // For allowed tags, strip dangerous attributes
      if (tag === 'a') {
        // For links, only allow href with http/https
        const hrefMatch = match.match(/href\s*=\s*["']([^"']+)["']/i);
        if (hrefMatch && /^https?:\/\//i.test(hrefMatch[1])) {
          return match.includes('</') ? '</a>' : `<a href="${hrefMatch[1]}" target="_blank" rel="noopener noreferrer">`;
        }
        return match.includes('</') ? '</a>' : '';
      }
      // For other tags, just keep the basic tag
      return match.includes('</') ? `</${tag}>` : `<${tag}>`;
    }
    return '';
  });

  return sanitized;
}

export default function InfoPopup() {
  const pathname = usePathname();
  const [config, setConfig] = useState<PopupConfig | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const sanitizedContent = useMemo(() => {
    if (!config?.content) return '';
    return sanitizeHtml(config.content);
  }, [config?.content]);

  // Don't show popup on admin pages
  const isAdminPage = pathname?.startsWith('/paneladminalibra');

  useEffect(() => {
    // Skip fetching if on admin page
    if (isAdminPage) return;

    const fetchConfig = async () => {
      try {
        const response = await fetch('/api/popup-config');
        const data = await response.json();
if (data.success && data.data?.enabled) {
          // Check if popup was already dismissed in this session
          if (!data.data.show_on_every_visit) {
            try {
              const dismissed = sessionStorage.getItem(POPUP_DISMISSED_KEY);
              if (dismissed) return;
            } catch (e) { /* private browsing */ }
          }

          setConfig(data.data);
          // Small delay for smooth animation
          setTimeout(() => setIsVisible(true), 100);

          // Set up auto-close if configured
          if (data.data.auto_close_seconds > 0) {
            setCountdown(data.data.auto_close_seconds);
          }
        }
      } catch (error) {
        console.error('Error fetching popup config:', error);
      }
    };

    fetchConfig();
  }, [isAdminPage]);

  // Handle auto-close countdown
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          handleClose();
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      if (config && !config.show_on_every_visit) {
        try { sessionStorage.setItem(POPUP_DISMISSED_KEY, 'true'); } catch (e) { /* private browsing */ }
      }
    }, 300);
  }, [config]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible && config?.show_close_button) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, config, handleClose]);

  if (!config || !isVisible || isAdminPage) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ backgroundColor: config.overlay_color }}
      onClick={(e) => {
        if (e.target === e.currentTarget && config.show_close_button) {
          handleClose();
        }
      }}
    >
      <div
        className={`relative rounded-xl shadow-2xl overflow-hidden transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{
          backgroundColor: config.background_color,
          color: config.text_color,
          maxWidth: config.width,
          width: '100%'
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
      >
        {/* Decorative top border */}
        <div
          className="h-1.5"
          style={{ backgroundColor: config.button_color }}
        />

        {/* Close button */}
        {config.show_close_button && (
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1.5 rounded-full transition-colors hover:bg-black/10"
            aria-label="Zamknij"
          >
            <HiXMark className="w-5 h-5" style={{ color: config.text_color }} aria-hidden="true" />
          </button>
        )}

        {/* Content */}
        <div className="p-6 pt-5">
          {config.title && (
            <h2
              id="popup-title"
              className="text-xl font-bold mb-3 pr-8"
              style={{ color: config.text_color }}
            >
              {config.title}
            </h2>
          )}

          {config.image_url && (
            <div className="mb-4">
              {/* Obraz wklejany w panelu admina jako dowolny URL — brak wymuszonej proporcji
                  (patrz paneladminalibra/config). width/height to tylko wskazówka rezerwacji
                  miejsca dla przeglądarki; realny rozmiar i tak kontroluje object-contain + max-h-64.
                  next/image (bez remotePatterns) optymalizuje tylko własną domenę — dla obcego
                  originu zostajemy przy zwykłym <img>. */}
              {isOwnOriginImage(config.image_url) ? (
                <Image
                  src={config.image_url}
                  alt=""
                  width={800}
                  height={450}
                  sizes="(max-width: 640px) 100vw, 512px"
                  className="w-full max-h-64 object-contain rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <img
                  src={config.image_url}
                  alt=""
                  width={800}
                  height={450}
                  className="w-full max-h-64 object-contain rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
            </div>
          )}

          <div
            className="prose prose-sm max-w-none mb-5 [&_a]:underline [&_a]:text-blue-600"
            style={{ color: config.text_color }}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />

          {/* Button with optional countdown */}
          <div className="flex items-center justify-end gap-3">
            {countdown !== null && countdown > 0 && (
              <span className="text-sm opacity-70">
                Automatyczne zamknięcie za {countdown}s
              </span>
            )}
            <button
              onClick={handleClose}
              className="px-5 py-2.5 rounded-lg font-medium transition-all hover:opacity-90 hover:shadow-md"
              style={{
                backgroundColor: config.button_color,
                color: config.button_text_color
              }}
            >
              {config.button_text}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
